import React from 'react';
import { uniq, get } from 'lodash';
import moment from 'moment';
import { CM_BASE_ROUTE } from '../../constants/cmConstants';
import { message, notification } from 'antd';
import {
  CloseOutlined,
  LoadingOutlined,
  CheckCircleTwoTone,
  CloseCircleOutlined
} from '@ant-design/icons';

// Goes back in browser history. If no history then goes to default path.
export const goBackOr = ({ history, defaultPath = '' }) => {
  if (history?.length <= 2) {
    return history.push(`${CM_BASE_ROUTE}${defaultPath}`);
  }
  history.goBack();
};

export const fromDaysToRangeBack = (numberOfDays) => {
  const now = moment();
  const fromMoment = now.clone().subtract(numberOfDays - 1, 'days');

  return {
    from: fromMoment.startOf('day').valueOf(),
    to: now.endOf('day').valueOf()
  };
};

export const fromDaysToRangeForward = (numberOfDays) => {
  const now = moment();
  const toMoment = now.clone().add(numberOfDays - 1, 'days');

  return {
    from: now.startOf('day').valueOf(),
    to: toMoment.endOf('day').valueOf()
  };
};

export const formatCheckerStatus = (statusCode) =>
  ({
    CHECK_NEEDED: '-',
    APPROVED: 'Approved',
    REJECTED: 'Rejected'
  }[statusCode]);

export const getUniqArrayBy = (list, getBy) =>
  uniq(list.map((item) => get(item, getBy)));

export const downloadFile = (fileData, fileName) => {
  const url = window.URL.createObjectURL(fileData);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  //Clean up link.
  window.URL.revokeObjectURL(url);
};

export const formatPageItemsRange = ({
  total,
  fromCount,
  toCount,
  itemsLabel = 'Record'
}) => {
  const toCountLabel = total > 1 ? ` - ${toCount}` : '';

  return `Showing ${fromCount}${toCountLabel} of ${total} ${itemsLabel}${
    total > 1 ? 's' : ''
  }`;
};

export const showNotification = ({
  msgKey,
  type,
  content,
  duration,
  close
}) => {
  let msg = close ? (
    <>
      <span dangerouslySetInnerHTML={{ __html: content }} />
      <CloseOutlined
        style={{ color: '#DF6057', margin: 0, top: '-1px' }}
        onClick={() => destoryNotification(msgKey)}
      />
    </>
  ) : (
    content
  );
  const config = {
    key: msgKey,
    type: type,
    content: msg,
    duration: duration
  };
  message.open(config);
};

const destoryNotification = (msgKey) => {
  message.destroy(msgKey);
};

export const showFileDownloadingNotification = ({ key, fileName }) => {
  notification.open({
    key,
    description: `Downloading ${fileName}`,
    duration: 0,
    icon: <LoadingOutlined style={{ color: '#1890FF' }} />
  });
};

export const showFileDownloadSucessNotification = ({ key, fileName }) => {
  notification.open({
    key,
    description: `${fileName} download completed`,
    icon: <CheckCircleTwoTone twoToneColor="#52c41a" />
  });
};

export const showFileDownloadFailedNotification = ({ key, fileName }) => {
  notification.open({
    key,
    description: `Failed to download ${fileName}`,
    icon: <CloseCircleOutlined style={{ color: '#eb2f96' }} />
  });
};

export const getFileNameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return null;

  // RFC 5987: filename*=UTF-8''<url-encoded-name> takes precedence over plain filename
  const starMatch = contentDisposition.match(/filename\*=UTF-8''([^\s;]+)/i);
  if (starMatch) {
    return decodeURIComponent(starMatch[1]);
  }

  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/);
  if (quotedMatch) return quotedMatch[1];

  const unquotedMatch = contentDisposition.match(/filename=([^\s;]+)/);
  return unquotedMatch ? unquotedMatch[1] : null;
};

export const validateNumberInput = (e) => {
  const pattern = new RegExp(/^\d+$/);
  return pattern.test(e.key) || e.key === '' ? e : e.preventDefault();
};

export const validateNumberDecimalInput = (e) => {
  const pattern = new RegExp(/(^\d+|\.)/);
  return pattern.test(e.key) || e.key === '' ? e : e.preventDefault();
};

export const validateNumberDecimalValidNumberInput = (text) => {
  const pattern = new RegExp(/(^\d+|\.)/);
  return pattern.test(text);
};

export const getInvalidCustomerIdChars = (value) => {
  // It includes comma because the value is comma separated.
  return value.match(/[^\p{L}\p{N}\p{M}!@#$%&*()\-_:|.;,/\\]/gu);
};

export const validateCustomerIdInput = (value) => {
  return !value.match(/[^\p{L}\p{N}\p{M}!@#$%&*()\-_:|.;/\\]+/gu);
};

export const getWatchlistOptions = (opts) => {
  const result = {
    watchlistCategory: [],
    watchlistSubCategory: [],
    watchlistKeyword: [],
    externalSource: [],
    watchlistSource: []
  };

  const fieldMappings = [
    {
      field: 'watchlistCategory',
      sourceKey: 'watchlistCategory'
    },
    {
      field: 'watchlistSubCategory',
      sourceKey: 'watchlistSubCategory'
    },
    {
      field: 'watchlistKeyword',
      sourceKey: 'keywords'
    },
    {
      field: 'externalSource',
      sourceKey: 'externalSources'
    },
    {
      field: 'watchlistSource',
      sourceKey: 'watchlistSource'
    }
  ];

  // Track both lowercase keys and their original case values
  const uniqueValues = fieldMappings.reduce((acc, { field }) => {
    acc[field] = new Map(); // Using Map to store both lower and original case
    return acc;
  }, {});

  opts.forEach((item) => {
    fieldMappings.forEach(({ field, sourceKey }) => {
      const value = item[sourceKey];
      if (value) {
        const trimmedValue = value.trim();
        const lowerValue = trimmedValue.toLowerCase();
        if (!uniqueValues[field].has(lowerValue)) {
          uniqueValues[field].set(lowerValue, trimmedValue);
          result[field].push({
            value: trimmedValue,
            label: trimmedValue
          });
        }
      }
    });
  });

  return result;
};

export const formatWatchlistOptions = (items) => {
  if (!Array.isArray(items)) return [];

  // Use a Map to track unique values (case-insensitive)
  const uniqueValues = new Map();

  return items.reduce((result, item) => {
    const trimmed = item?.toString().trim() || '';
    if (!trimmed) return result;

    const lower = trimmed.toLowerCase();

    // Skip if we've already seen this value (case-insensitive)
    if (!uniqueValues.has(lower)) {
      uniqueValues.set(lower, true);
      result.push({
        label: trimmed,
        value: trimmed
      });
    }

    return result;
  }, []);
};

export const getUniqueWatchlistOptions = (existingData, options) => {
  // Create a Set of existing values for O(1) lookups
  const existingValues = new Set(
    existingData.map((item) => item.value.toLowerCase())
  );

  // Filter out any new options that already exist in the current data
  const uniqueNewOptions = options.filter(
    (option) => !existingValues.has(option.value.toLowerCase())
  );

  return [...existingData, ...uniqueNewOptions];
};
